import React, { useContext } from "react";
import { Flex, Tag } from "antd";
import { Handle, Position } from "reactflow";
import { AppContext } from "../../../context/AppContext";
import { actionCodeToTitle, getActionIconAndClass, getFormTypeForAction } from "../utils/actionCodeMapping";
import { NOTIFY_USER_MESSAGE_FALLBACK } from "../utils/notifyUserStaticMessage";
import {
    resolveLogInCrmActionNodeSummaryMode,
    type LogInCrmActionNodeSummaryMode,
} from "../utils/workflowCrmActionOverrides";
import { useWorkflowStore } from "../store";

export interface ActionNodeData {
    actionType?: string;
    actionCode?: string;
    title?: string;
    isPlaceholder?: boolean;
    message?: string;
    onceOnly?: boolean;
    responseType?: string;
    templateId?: string;
    templateName?: string;
    templateIntegId?: string;
    hasMedia?: boolean;
    mediaFileList?: unknown[];
    components?: unknown;
    fieldNameAndValueMap?: Record<string, unknown>;

    recordOwner?: boolean;

    accountOwner?: boolean;

    includeAdditionalEmails?: boolean;

    additionalEmails?: string[];

    logRecordAsThread?: string;
    logRecordsFor?: string;
    loggingPreference?: string | boolean;
    logRecordsOnStatus?: string;
    activityType?: string;

    delayDays?: number;
    delayHours?: number;
    delayMinutes?: number;

    createLead?: string;

    [key: string]: unknown;
}

const ACTION_META: Record<string, { defaultTitle: string }> = {
    AutomatedResponse: { defaultTitle: "Automated Response" },
    NotifyUser: { defaultTitle: "Notify User" },
    CreateLead: { defaultTitle: "Create Lead" },
    LogInCrm: { defaultTitle: "Log in CRM" },
    Delay: { defaultTitle: "Delay" },
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="d-flex align-items-start gap-2">
        <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian text-nowrap flex-shrink-0">{label}</span>
        <Flex gap="small" align="start" className="hs-fs-12 hs-fw-400 hs-color-BlackRussian flex-wrap min-w-0 flex-grow-1">
            {children}
        </Flex>
    </div>
);

const Badge: React.FC<{ children: React.ReactNode; className?: string; title?: string }> = ({ children, className, title }) => (
    <span title={title} className={`hs-fs-12 hs-text-truncate mw-100 rounded-1 hs-color-BlackRussian ${className ?? ""}`.trim()}>{children}</span>
);

function hasTemplateComponents(components: unknown): boolean {
    if (components === undefined || components === null) return false;
    if (Array.isArray(components)) return components.length > 0;
    if (typeof components === "object") return Object.keys(components as Record<string, unknown>).length > 0;
    return false;
}

function messageBodyLooksLikeUrl(message: string): boolean {
    return /^https?:\/\//i.test(message.trim());
}

const DEFAULT_NODE_ICON = { icon: <div className="automationIconsSprite select-icon" /> };

function getNodeIcon(data: ActionNodeData) {
    if (!data.actionCode && !data.actionType) return DEFAULT_NODE_ICON;
    return getActionIconAndClass(data.actionCode ?? data.actionType);
}

function getActionFormType(data: ActionNodeData): string {
    return getFormTypeForAction(data.actionCode ?? data.actionType ?? "");
}

function logRecordsScopeSummaryLabel(logRecordsFor: string | undefined): string | null {
    if (!logRecordsFor) return null;
    return logRecordsFor === "recent" ? "Most recent record only" : "All matching records";
}

function isLogInCrmCheckboxStyleLogging(loggingPreference: ActionNodeData["loggingPreference"]): boolean {
    return (
        typeof loggingPreference === "boolean" ||
        loggingPreference === "true" ||
        loggingPreference === "false"
    );
}

function formatLogInCrmSimpleCanvasSummaryValue(data: ActionNodeData): string {
    const loggingPreferenceValue = data.loggingPreference;
    if (typeof loggingPreferenceValue === "boolean") {
        return loggingPreferenceValue ? "Yes" : "No";
    }
    if (loggingPreferenceValue === "true" || loggingPreferenceValue === "false") {
        return loggingPreferenceValue === "true" ? "Yes" : "No";
    }
    if (loggingPreferenceValue === "activities") return "Activities";
    if (loggingPreferenceValue === "notes") return "Notes";
    if (typeof loggingPreferenceValue === "string" && loggingPreferenceValue.trim() !== "") {
        return loggingPreferenceValue;
    }
    return "Yes";
}

function getLogInCrmNodeSummary(data: ActionNodeData): { loggingSummaryLabel: string; showActivitiesDetailRows: boolean } {
    const loggingPreferenceValue = data.loggingPreference;

    if (loggingPreferenceValue === "activities") {
        return { loggingSummaryLabel: "Activities", showActivitiesDetailRows: true };
    }

    if (loggingPreferenceValue === "notes") {
        return {
            loggingSummaryLabel: "Notes",
            showActivitiesDetailRows: false,
        };
    }

    if (typeof loggingPreferenceValue === "boolean") {
        return { loggingSummaryLabel: loggingPreferenceValue ? "Yes" : "No", showActivitiesDetailRows: false };
    }

    if (loggingPreferenceValue === "true" || loggingPreferenceValue === "false") {
        return {
            loggingSummaryLabel: loggingPreferenceValue === "true" ? "Yes" : "No",
            showActivitiesDetailRows: false,
        };
    }

    if (typeof loggingPreferenceValue === "string" && loggingPreferenceValue.trim() !== "") {
        return { loggingSummaryLabel: loggingPreferenceValue, showActivitiesDetailRows: false };
    }

    return {
        loggingSummaryLabel: "Notes",
        showActivitiesDetailRows: false,
    };
}

const renderActionDetails = (
    data: ActionNodeData,
    logInCrmCanvasSummaryMode: LogInCrmActionNodeSummaryMode,
    applyOncePerContact?: boolean | null,
    notifyUserStaticMessage?: string
) => {
    const formType = getActionFormType(data);
    switch (formType) {
        case "AutomatedResponse": {
            const usesTemplate = Boolean(
                (data.templateId && data.templateId.trim() !== "") ||
                    (data.templateName && data.templateName.trim() !== "") ||
                    hasTemplateComponents(data.components)
            );
            const templateTitle =
                data.templateName?.trim() || (usesTemplate ? "Selected template" : "");
            const trimmedMessageBody = data.message?.trim() ?? "";
            const bodyIsUrl = trimmedMessageBody.length > 0 && messageBodyLooksLikeUrl(trimmedMessageBody);
            const showPlainTextMessage =
                !usesTemplate && trimmedMessageBody.length > 0 && !(data.hasMedia && bodyIsUrl);
            const showMediaOnly =
                !usesTemplate && data.hasMedia && (!trimmedMessageBody || bodyIsUrl);
            return (
                <>
                    {usesTemplate && templateTitle && (
                        <DetailRow label="Template:">
                            <Badge title={templateTitle}>{templateTitle}</Badge>
                        </DetailRow>
                    )}
                    {data.responseType && (
                        <DetailRow label="Response:">
                            <Badge title={data.responseType}>{data.responseType}</Badge>
                        </DetailRow>
                    )}
                    {showPlainTextMessage && (
                        <DetailRow label="Message:">
                            <span className="trigger-rule-node-detail-value-message overflow-hidden text-break flex-fill font-italic hs-color-BlackRussian">
                                &ldquo;{trimmedMessageBody}&rdquo;
                            </span>
                        </DetailRow>
                    )}
                    {showMediaOnly && (
                        <DetailRow label="Message:">
                            <Badge>Media files attached</Badge>
                        </DetailRow>
                    )}
                    {(applyOncePerContact ?? data.onceOnly) && (
                        <DetailRow label="Apply once per contact:">
                            <Badge>Yes</Badge>
                        </DetailRow>
                    )}
                </>
            );
        }
        case "NotifyUser":
            const recipients: string[] = [];
            if (data.recordOwner) recipients.push("Record Owner");
            if (data.accountOwner) recipients.push("Account Owner");
            if (data.includeAdditionalEmails && data.additionalEmails?.length) {
                recipients.push(...data.additionalEmails);
            }
            return (
                <>
                    {recipients.length > 0 && (
                        <DetailRow label="Send to:">
                            <div className="d-flex flex-wrap gap-1 mw-100">
                                {recipients.map((recipientDisplayLabel, recipientListIndex) => (
                                    <Tag
                                        key={`${recipientDisplayLabel}-${recipientListIndex}`}
                                        title={recipientDisplayLabel}
                                        className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0 mw-100 hs-text-truncate"
                                    >
                                        {recipientDisplayLabel}
                                    </Tag>
                                ))}
                            </div>
                        </DetailRow>
                    )}
                    <DetailRow label="Message:">
                        <span className="trigger-rule-node-detail-value-message overflow-hidden text-break flex-fill font-italic hs-color-BlackRussian">
                            &ldquo;
                            {notifyUserStaticMessage?.trim()
                                ? notifyUserStaticMessage
                                : NOTIFY_USER_MESSAGE_FALLBACK}
                            &rdquo;
                        </span>
                    </DetailRow>
                </>
            );
        case "LogInCrm": {
            if (logInCrmCanvasSummaryMode === "simple") {
                return (
                    <DetailRow label="Log messages in the CRM:">
                        <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                            {formatLogInCrmSimpleCanvasSummaryValue(data)}
                        </span>
                    </DetailRow>
                );
            }
            const { loggingSummaryLabel, showActivitiesDetailRows } = getLogInCrmNodeSummary(data);
            const shouldShowLogToWhichRecordsRow =
                Boolean(data.logRecordsFor) && !isLogInCrmCheckboxStyleLogging(data.loggingPreference);
            const logToRecordsHumanReadableLabel = logRecordsScopeSummaryLabel(data.logRecordsFor);
            return (
                <>
                    <DetailRow label="Logging:">
                        <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">{loggingSummaryLabel}</span>
                    </DetailRow>
                    {shouldShowLogToWhichRecordsRow && logToRecordsHumanReadableLabel && (
                        <DetailRow label="Log to:">
                            <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                                {logToRecordsHumanReadableLabel}
                            </span>
                        </DetailRow>
                    )}
                    {data.logRecordsOnStatus?.trim() && (
                        <DetailRow label="Outbound status:">
                            <Badge title={data.logRecordsOnStatus}>{data.logRecordsOnStatus}</Badge>
                        </DetailRow>
                    )}
                    {showActivitiesDetailRows && data.activityType?.trim() && (
                        <DetailRow label="Activity type:">
                            <Badge title={data.activityType}>{data.activityType}</Badge>
                        </DetailRow>
                    )}
                    {showActivitiesDetailRows && data.logRecordAsThread && (
                        <DetailRow label="Merge activities:">
                            <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                                {data.logRecordAsThread === "true" ? "Yes" : "No"}
                            </span>
                        </DetailRow>
                    )}
                </>
            );
        }
        case "CreateLead":
            return (
                <DetailRow label="Auto-create lead:">
                    <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                        {data.createLead === "false" ? "No" : "Yes"}
                    </span>
                </DetailRow>
            );
        case "Delay": {
            const delayDaysCount = data.delayDays ?? 0;
            const delayHoursCount = data.delayHours ?? 0;
            const delayMinutesCount = data.delayMinutes ?? 0;
            const delayParts: string[] = [];
            if (delayDaysCount > 0) delayParts.push(`${delayDaysCount}d`);
            if (delayHoursCount > 0) delayParts.push(`${delayHoursCount}h`);
            if (delayMinutesCount > 0) delayParts.push(`${delayMinutesCount}m`);
            const delaySummaryLabel = delayParts.length > 0 ? delayParts.join(" ") : "—";
            return (
                <DetailRow label="Wait for:">
                    <Badge>{delaySummaryLabel}</Badge>
                </DetailRow>
            );
        }
        default:
            return null;
    }
};

function getActionMeta(data: ActionNodeData) {
    const formTypeKeyForDefaultTitle = getActionFormType(data);
    return ACTION_META[formTypeKeyForDefaultTitle] ?? null;
}

const ActionNodeComponent: React.FC<{ data: ActionNodeData; selected?: boolean }> = ({ data, selected }) => {
    const [appProperties] = useContext(AppContext);
    const hasTypeSelected = !!(data.actionCode || data.actionType);
    const isPlaceholder = data.isPlaceholder ?? !hasTypeSelected;
    const actionTitleMeta = getActionMeta(data);
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);
    const notifyUserStaticMessage = useWorkflowStore((s) => s.notifyUserStaticMessage);
    const logInCrmCanvasSummaryMode = resolveLogInCrmActionNodeSummaryMode(appProperties?.controller);

    const displayTitle = isPlaceholder
        ? "Select an Action..."
        : (data.title ?? actionTitleMeta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action");

    return (
        <div
            className={`hs-w-350-px position-relative hs-border-grey rounded-4 p-3 ${
                isPlaceholder
                    ? `trigger-rule-node-placeholder bg-transparent ${selected ? "hs-border-violet" : "hs-border-color-placeholder"}`
                    : `hs-bg-plain-white${selected ? " hs-border-violet" : ""}`
            }`}
            role="button"
            tabIndex={0}
            aria-label={`Action: ${displayTitle}. ${selected ? "Selected" : "Click to select"}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                }
            }}
        >
            <Handle type="target" position={Position.Top} id="target" className="opacity-0"/>
            <Handle type="source" position={Position.Bottom} id="source" className="opacity-0"/>
            <span className={`trigger-rule-node-tag hs-fs-11 hs-fw-600 rounded-3 border-1 hs-border-violet py-1 px-3 ${isPlaceholder ? "tag-fill" : "hs-color-violet hs-bg-plain-white"}`}>{isPlaceholder ? "+ NEW STEP" : "Action"}</span>

            {isPlaceholder ? (
                <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center justify-content-center">
                        {getNodeIcon(data).icon}
                    </div>
                    <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0">
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">Select an Action...</span>
                        <span className="hs-fs-12 hs-color-medium-dark">Choose from the panel on the right</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center justify-content-center">
                            {getNodeIcon(data).icon}
                        </div>
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">
                            {data.title ?? actionTitleMeta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action"}
                        </span>
                    </div>
                    <Flex vertical gap="middle" justify="space-between" className="pt-3">
                        {renderActionDetails(data, logInCrmCanvasSummaryMode, applyOncePerContact, notifyUserStaticMessage)}
                    </Flex>
                </>
            )}
        </div>
    );
};

export const ActionNode = React.memo(ActionNodeComponent);
